import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    IconButton,
    Typography,
    Box,
    Tooltip,
    Chip,
} from '@mui/material';
import { MoreVert as MoreIcon, PictureAsPdfOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import useCurrency from '../../../hooks/common/useCurrency';
import OrderStatusBadge from './OrderStatusBadge';
import PaymentStatusBadge from './PaymentStatusBadge';

dayjs.extend(relativeTime);

const PAYMENT_METHOD_LABELS = {
    cod:        { ar: 'الدفع عند الاستلام', en: 'COD' },
    paymob:     { ar: 'بايموب',              en: 'Paymob' },
    manual:     { ar: 'يدوي',                en: 'Manual' },
    knet:       { ar: 'كي-نت',               en: 'KNET' },
    tap:        { ar: 'Tap',                 en: 'Tap' },
    myfatoorah: { ar: 'MyFatoorah',          en: 'MyFatoorah' },
};

const OrdersTableRow = ({
    item,
    index = 0,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
    onPreviewInvoice,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const lastPayment = Array.isArray(item.payments) && item.payments.length
        ? item.payments[item.payments.length - 1]
        : null;
    const paymentMethod = item.payment_method || lastPayment?.provider || lastPayment?.method;
    const paymentStatus = item.payment_status || lastPayment?.status;
    const paymentMethodLabel =
        (PAYMENT_METHOD_LABELS[paymentMethod] || {})[lng] || paymentMethod || '-';

    const shippingMethodName =
        item.shippingMethod?.name?.[lng] ||
        item.shippingMethod?.name?.en ||
        item.shipping_method?.name?.[lng] ||
        item.shipping_method?.name?.en ||
        '-';

    const customerName =
        item.user?.name ||
        item.customer?.name ||
        (item.user_id ? `User #${item.user_id}` : '-');
    const customerEmail = item.user?.email || item.customer?.email;
    const placedAt = item.placed_at || item.created_at;
    const itemsCount = item.items_count ?? item.items?.length ?? 0;

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isSelected}
                    onClick={(e) => onRowCheckboxClick(e, item.id)}
                />
            </TableCell>

            {/* S/N */}
            <TableCell align="start">{index + 1}</TableCell>

            {/* Order # */}
            <TableCell>
                <Typography
                    component={RouterLink}
                    to={`/orders/${item.id}`}
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                    {item.order_number || `#${item.id}`}
                </Typography>
            </TableCell>

            {/* Customer */}
            <TableCell>
                <Box>
                    <Typography variant="body2" fontWeight="medium">{customerName}</Typography>
                    {customerEmail && (
                        <Typography variant="caption" color="text.secondary">{customerEmail}</Typography>
                    )}
                </Box>
            </TableCell>

            {/* Items count */}
            <TableCell align="center">
                <Chip
                    size="small"
                    label={itemsCount}
                    variant="tonal"
                    color={itemsCount > 0 ? 'primary' : 'default'}
                    sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: '40px' }}
                />
            </TableCell>

            {/* Payment */}
            <TableCell align="center">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{paymentMethodLabel}</Typography>
                    {paymentStatus && <PaymentStatusBadge status={paymentStatus} />}
                </Box>
            </TableCell>

            {/* Shipping method */}
            <TableCell align="center">
                <Typography variant="body2">{shippingMethodName}</Typography>
            </TableCell>

            {/* Total */}
            <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                    {formatPrice(item.grand_total ?? 0)}
                </Typography>
            </TableCell>

            {/* Status */}
            <TableCell align="center">
                <OrderStatusBadge status={item.status} />
            </TableCell>

            {/* Placed at */}
            <TableCell align="center">
                {placedAt ? (
                    <Tooltip title={dayjs(placedAt).format('YYYY-MM-DD HH:mm')}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {dayjs(placedAt).fromNow()}
                        </Typography>
                    </Tooltip>
                ) : '-'}
            </TableCell>

            {/* Actions */}
            <TableCell align="center">
                <Stack direction="row" spacing={0.25} justifyContent="center">
                    {onPreviewInvoice && (
                        <Tooltip title={isArabic ? 'معاينة الفاتورة' : 'Preview invoice'}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPreviewInvoice(item);
                                }}
                            >
                                <PictureAsPdfOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton size="small" onClick={(e) => onMenuOpen(e, item)}>
                        <MoreIcon />
                    </IconButton>
                </Stack>
            </TableCell>
        </TableRow>
    );
};

export default OrdersTableRow;
